import cx from "classnames";
import { FC, MouseEventHandler, ReactNode, SVGProps } from "react";

import { Icon } from "../icon/Icon";

type TextButtonVariant = "info" | "danger" | "default" | "secondary";

interface TextButtonProps {
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
  /** Label */
  "children": ReactNode;
  /** Optionally disable the button. */
  "disabled"?: boolean;
  /** Callback to handle button click events */
  "onClick"?: MouseEventHandler<HTMLButtonElement>;
  /** Add an icon before the label. */
  "startIconSVG"?: FC<SVGProps<SVGSVGElement>>;
  /** Add an icon after the label. */
  "endIconSVG"?: FC<SVGProps<SVGSVGElement>>;
  /** If true, button will submit parent form */
  "submit"?: boolean;
  /** If true, font size is xs (12px) */
  "xsText"?: boolean;
  /** Optional override and/or extension of classes */
  "className"?: string;
  /** Button variant for styling */
  "variant"?: TextButtonVariant;
}

const getVariantClasses = (variant: TextButtonVariant): string => {
  switch (variant) {
    case "danger":
      return "text-red";
    case "info":
      return "text-black";
    case "secondary":
      return "text-secondary";
    case "default":
    default:
      return "text-brand-color";
  }
};

/**
 * Atomic component - TextButton
 */
export const TextButton = ({
  children,
  disabled,
  onClick,
  endIconSVG,
  startIconSVG,
  submit = false,
  xsText = false,
  className,
  variant = "default",
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
}: TextButtonProps) => {
  return (
    <button
      type={submit ? "submit" : "button"}
      className={cx(
        "flex items-center justify-center text-button active:opacity-60 disabled:opacity-20",
        variant === "secondary"
          ? "cursor-pointer border-none bg-transparent !py-0 text-center font-sans text-body1 font-normal transition-opacity hover:opacity-70"
          : "",
        getVariantClasses(variant),
        startIconSVG || endIconSVG ? "py-2.5" : "py-[13px]",
        startIconSVG ? "pl-1" : "pl-3",
        endIconSVG ? "pr-1" : null,
        xsText && "text-xs",
        className,
      )}
      disabled={disabled}
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
    >
      {startIconSVG && (
        <Icon svg={startIconSVG} color="currentColor" size={xsText ? "x3small" : "xsmall"} />
      )}
      {children}
      {endIconSVG && (
        <Icon svg={endIconSVG} color="currentColor" size={xsText ? "x3small" : "xsmall"} />
      )}
    </button>
  );
};
